import { useRef, useState } from "react";
import { apiRequest } from "../../services/api";
import SisSupplierCreditRelease from "./SisSupplierCreditRelease";
import SisSupplierCreditHistory from './SisSupplierCreditHistory';
const control = "mt-1 w-full rounded-lg border p-2 text-sm";
const sourceId = s => s.sisRemainingCorrectionId || s.sisAdjustmentId || s.sisReturnId;
const sourceKey = s => s.sisRemainingCorrectionId ? `REMAINING:${s.sisRemainingCorrectionId}` : s.sisAdjustmentId ? `ADJUSTMENT:${s.sisAdjustmentId}` : s.sisReturnId;
const sourceLabel = s => s.sisRemainingCorrectionId ? 'post-return dues reduction' : s.sisAdjustmentId ? 'dues reduction' : 'return';
const fields = {
  number: "Supplier credit-note number",
  documentDate: "Credit-note date",
  supplierGstin: "Supplier credit-note GSTIN",
  taxableAmount: "Credit-note taxable amount",
  cgst: "Credit-note CGST",
  sgst: "Credit-note SGST",
  igst: "Credit-note IGST",
  supplierInvoiceReference: "Original supplier invoice reference",
  evidenceReference: "Credit-note evidence reference",
  reviewNote: "Credit-note document review",
};
export default function SisSupplierCreditNotes({
  base,
  registrationId,
  bills,
  accessToken,
  allowed,
  canRelease,
}) {
  const suppliers = [
    ...new Map(
      bills.map((b) => [
        b.supplierId,
        { id: b.supplierId, name: b.supplierName || b.supplierId },
      ]),
    ).values(),
  ];
  const [open, setOpen] = useState(false),
    [supplierId, setSupplierId] = useState(suppliers[0]?.id || ""),
    [data, setData] = useState(null),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [mode, setMode] = useState("note"),
    [noteId, setNoteId] = useState(""),
    [amounts, setAmounts] = useState({}),
    [reason, setReason] = useState(""),
    [values, setValues] = useState({
      number: "",
      documentDate: new Date(Date.now() + 19800000).toISOString().slice(0, 10),
      supplierGstin: "",
      taxableAmount: "0",
      cgst: "0",
      sgst: "0",
      igst: "0",
      supplierInvoiceReference: "",
      evidenceReference: "",
      reviewNote: "",
    }),
    [proposal, setProposal] = useState(null),
    [checks, setChecks] = useState({}),
    [notice, setNotice] = useState("");
  const pending = useRef(false),
    key = useRef(null);
  function invalidate() {
    setProposal(null);
    setChecks({});
    key.current = null;
    setError("");
    setNotice("");
  }
  async function load() {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    setError("");
    try {
      setData(
        await apiRequest(
          `${base}/supplier-credit-notes?supplierId=${encodeURIComponent(supplierId)}&registrationId=${encodeURIComponent(registrationId)}`,
          { accessToken },
        ),
      );
    } catch (e) {
      setError(e.message);
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }
  async function preview() {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    invalidate();
    try {
      const input =
          mode !== "match"
            ? {
                ...values,
                supplierId,
                registrationId,
                ...(mode === "replace"
                  ? {
                      date: new Date(Date.now() + 19800000)
                        .toISOString()
                        .slice(0, 10),
                      replacementReason: reason,
                    }
                  : {}),
              }
            : {
                date: new Date(Date.now() + 19800000)
                  .toISOString()
                  .slice(0, 10),
                reason,
                allocations: Object.entries(amounts)
                  .filter(([, v]) => v.trim() !== "")
                  .map(([id, amount]) => id.startsWith('REMAINING:') ? { sisRemainingCorrectionId: id.slice(10), amount } : id.startsWith('ADJUSTMENT:') ? { sisAdjustmentId: id.slice(11), amount } : { sisReturnId: id, amount }),
              },
        url =
          mode === "replace"
            ? `${base}/supplier-credit-notes/${noteId}/replacement-preview`
            : mode === "note"
              ? `${base}/supplier-credit-notes/preview`
              : `${base}/supplier-credit-notes/${noteId}/match-preview`,
        r = await apiRequest(url, { method: "POST", accessToken, body: input });
      setProposal({ ...r, input, mode, noteId });
    } catch (e) {
      setError(e.message);
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }
  async function save() {
    if (
      pending.current ||
      !allowed ||
      !proposal ||
      !checks.first ||
      !checks.second ||
      (proposal?.mode === "replace" && (!canRelease || !checks.third))
    )
      return;
    pending.current = true;
    setBusy(true);
    setError("");
    try {
      key.current ||= window.crypto.randomUUID();
      const note = proposal.mode !== "match",
        body = {
          ...proposal.input,
          previewHash: proposal.previewHash,
          requestKey: key.current,
          ...(note
            ? {
                actualDocumentReviewed: true,
                noFinancialPostingConfirmed: true,
                ...(proposal.mode === "replace"
                  ? { supersessionReviewed: true }
                  : {}),
              }
            : {
                returnCreditsReviewed: true,
                noDoubleCreditOrRefundConfirmed: true,
              }),
        };
      await apiRequest(
        proposal.mode === "replace"
          ? `${base}/supplier-credit-notes/${proposal.noteId}/replacements`
          : note
            ? `${base}/supplier-credit-notes`
            : `${base}/supplier-credit-notes/${proposal.noteId}/matches`,
        { method: "POST", accessToken, body },
      );
      setNotice(
        proposal.mode === "replace"
          ? "Supplier credit document superseded. Original evidence retained; new version awaits matching. No financial entry."
          : note
            ? "Supplier credit note retained as unmatched evidence. Select it under Match existing credit note."
            : "Document match retained. No additional supplier credit, tax or refund was posted.",
      );
      setProposal(null);
      setChecks({});
      setAmounts({});
      key.current = null;
      setData(
        await apiRequest(
          `${base}/supplier-credit-notes?supplierId=${encodeURIComponent(supplierId)}&registrationId=${encodeURIComponent(registrationId)}`,
          { accessToken },
        ),
      );
    } catch (e) {
      setError(e.message);
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }
  return (
    <section className="rounded-xl border border-blue-200 p-4">
      <button
        type="button"
        disabled={busy}
        className="font-semibold text-blue-900"
        onClick={() => setOpen((v) => !v)}
      >
        Supplier credit-note reconciliation
      </button>
      {open && (
        <div className="mt-3 space-y-4 text-sm">
          <p>
            Match actual supplier documents to return or dues-reduction credits already posted.
            Missing evidence stays outstanding; excess document amounts stay
            unmatched for review. Nothing here pays a refund, posts input GST or
            reduces dues again.
          </p>
          <label className="block">
            Credit-note supplier
            <select
              disabled={busy}
              className={control}
              value={supplierId}
              onChange={(e) => {
                setSupplierId(e.target.value);
                setData(null);
                setNoteId("");
                setAmounts({});
                invalidate();
              }}
            >
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            disabled={busy || !supplierId}
            onClick={load}
            className="rounded border px-3 py-2"
          >
            Refresh supplier credit evidence
          </button>
          <SisSupplierCreditHistory key={`${base}:${supplierId}:${registrationId}:${accessToken}`} base={base} supplierId={supplierId} registrationId={registrationId} accessToken={accessToken}/>
          {error && (
            <p role="alert" className="text-red-700">
              {error}
            </p>
          )}
          {notice && (
            <p role="status" className="text-green-800">
              {notice}
            </p>
          )}
          {data && (
            <>
              <h4 className="font-semibold">
                Recorded supplier credits awaiting documents
              </h4>
              {!data.sources.length && (
                <p>No recorded supplier credits for this scope.</p>
              )}
              <table className="w-full text-left">
                <thead>
                  <tr>
                    <th>Credit source</th>
                    <th>Credit</th>
                    <th>Matched</th>
                    <th>Awaiting evidence</th>
                  </tr>
                </thead>
                <tbody>
                  {data.sources.map((s) => (
                    <tr key={sourceKey(s)}>
                      <td>
                        {s.reference} · {s.returnDate}
                        <span
                          className="block text-xs text-gray-500"
                          title={sourceId(s)}
                        >
                          {sourceLabel(s)} {sourceId(s).slice(0, 8)}
                        </span>
                      </td>
                      <td>₹{s.total}</td>
                      <td>₹{s.matched}</td>
                      <td>₹{s.unmatched}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <h4 className="font-semibold">Retained supplier documents</h4>
              {!data.notes.length && <p>No supplier documents received.</p>}
              {data.notes.map((n) => (
                <div key={n.id} className="rounded border p-2">
                  <p>
                    {n.number} · {n.snapshot.documentDate} · ₹{n.total} ·{" "}
                    {n.status}
                    {n.supersededBy && (
                      <strong className="block text-amber-800">
                        Superseded — historical evidence only. Current successor{" "}
                        {n.supersededBy}
                      </strong>
                    )}
                    {n.supersedesNoteId && (
                      <span className="block">
                        Replaces {n.supersedesNoteId} ·{" "}
                        {n.snapshot.replacementReason}
                      </span>
                    )}
                  </p>
                  {n.supersededBy ? <p>Historical document amount only — no active matching availability.</p> : <p>
                    Matched ₹{n.matched} · Unmatched document ₹{n.unmatched} —
                    not an additional accepted credit or refund
                  </p>}
                  <details>
                    <summary>Document evidence and match history</summary>
                    <p>
                      {n.snapshot.supplierInvoiceReference} ·{" "}
                      {n.snapshot.evidenceReference}
                    </p>
                    <p>{n.snapshot.reviewNote}</p>
                    <p>
                      Supplier GSTIN {n.snapshot.supplierGstin} · Taxable ₹
                      {n.snapshot.taxableAmount} · CGST ₹{n.snapshot.cgst} ·
                      SGST ₹{n.snapshot.sgst} · IGST ₹{n.snapshot.igst}
                    </p>
                    <p>
                      Retained by {n.createdBy} · {n.createdAt}
                    </p>
                    {data.matches
                      .filter((m) => m.noteId === n.id)
                      .map((m) => (
                        <div key={m.id} className="mt-2 border-t pt-2">
                          <p>
                            {m.snapshot.date} · ₹{m.amount} ·{" "}
                            {m.snapshot.reason} · Recorded by {m.createdBy}
                          </p>
                          {m.snapshot.allocations.map((a) => (
                            <p
                              key={sourceKey(a)}
                              className="break-all text-xs"
                            >
                              {sourceLabel(a)} {sourceId(a)} · historical match ₹
                              {a.amount}
                            </p>
                          ))}
                          <SisSupplierCreditRelease
                            match={m}
                            base={base}
                            accessToken={accessToken}
                            allowed={allowed && canRelease}
                            blocked={busy}
                            onBusyChange={(value) => {
                              pending.current = value;
                              setBusy(value);
                            }}
                            onReleased={async () => {
                              setNotice(
                                "Incorrect document match released. Availability restored; no financial entry or refund.",
                              );
                              try {
                                setData(
                                  await apiRequest(
                                    `${base}/supplier-credit-notes?supplierId=${encodeURIComponent(supplierId)}&registrationId=${encodeURIComponent(registrationId)}`,
                                    { accessToken },
                                  ),
                                );
                              } catch (e) {
                                setError(
                                  `Release recorded. Refresh evidence to reload balances: ${e.message}`,
                                );
                              }
                            }}
                          />
                        </div>
                      ))}
                  </details>
                </div>
              ))}
            </>
          )}
          {allowed ? (
            <fieldset disabled={busy} className="space-y-3">
              <legend className="font-semibold">
                Review supplier evidence
              </legend>
              <label className="block">
                Credit-note action
                <select
                  className={control}
                  value={mode}
                  onChange={(e) => {
                    setMode(e.target.value);
                    invalidate();
                  }}
                >
                  <option value="note">Retain a received credit note</option>
                  <option value="match">Match existing credit note</option>
                  {canRelease && (
                    <option value="replace">
                      Correct / supersede a credit note
                    </option>
                  )}
                </select>
              </label>
              {mode === "replace" && (
                <>
                  <p>
                    Release all active matches first. This retains a new version
                    of the same supplier document; it does not record an
                    additional document or alter dues. Supplier, registration
                    and financial year cannot change.
                  </p>
                  <label className="block">
                    Credit note to supersede
                    <select
                      className={control}
                      value={noteId}
                      onChange={(e) => {
                        setNoteId(e.target.value);
                        const n = data?.notes.find(
                          (n) => n.id === e.target.value,
                        );
                        if (n)
                          setValues(
                            Object.fromEntries(
                              Object.keys(fields).map((k) => [
                                k,
                                n.snapshot[k] || "",
                              ]),
                            ),
                          );
                        invalidate();
                      }}
                    >
                      <option value="">
                        Choose unmatched current document
                      </option>
                      {data?.notes
                        .filter(
                          (n) => !n.supersededBy && Number(n.matched) === 0,
                        )
                        .map((n) => (
                          <option key={n.id} value={n.id}>
                            {n.number} · ₹{n.total}
                          </option>
                        ))}
                    </select>
                  </label>
                  <label className="block">
                    Document replacement reason
                    <textarea
                      className={control}
                      maxLength={1000}
                      value={reason}
                      onChange={(e) => {
                        setReason(e.target.value);
                        invalidate();
                      }}
                    />
                  </label>
                </>
              )}
              {mode !== "match" ? (
                Object.entries(fields).map(([k, label]) => (
                  <label key={k} className="block">
                    {label}
                    <input
                      className={control}
                      type={k === "documentDate" ? "date" : "text"}
                      maxLength={k === "reviewNote" ? 1000 : 200}
                      value={values[k]}
                      onChange={(e) => {
                        setValues((v) => ({ ...v, [k]: e.target.value }));
                        invalidate();
                      }}
                    />
                  </label>
                ))
              ) : (
                <>
                  <label className="block">
                    Credit note to match
                    <select
                      className={control}
                      value={noteId}
                      onChange={(e) => {
                        setNoteId(e.target.value);
                        setAmounts({});
                        invalidate();
                      }}
                    >
                      <option value="">
                        Choose retained supplier document
                      </option>
                      {data?.notes
                        .filter((n) => !n.supersededBy)
                        .map((n) => (
                          <option key={n.id} value={n.id}>
                            {n.number} · Unmatched ₹{n.unmatched}
                          </option>
                        ))}
                    </select>
                  </label>
                  {data?.sources.map((s) => (
                    <label key={sourceKey(s)} className="block">
                      Match amount for {s.reference} · {sourceLabel(s)}{" "}
                      {sourceId(s).slice(0, 8)} (remaining ₹{s.unmatched})
                      <input
                        className={control}
                        inputMode="decimal"
                        value={amounts[sourceKey(s)] || ""}
                        onChange={(e) => {
                          setAmounts((v) => ({
                            ...v,
                            [sourceKey(s)]: e.target.value,
                          }));
                          invalidate();
                        }}
                      />
                    </label>
                  ))}
                  <label className="block">
                    Credit match reason
                    <textarea
                      className={control}
                      value={reason}
                      maxLength={1000}
                      onChange={(e) => {
                        setReason(e.target.value);
                        invalidate();
                      }}
                    />
                  </label>
                </>
              )}
              <button
                type="button"
                disabled={
                  busy ||
                  (mode === "match" &&
                    (!noteId || reason.trim().length < 20)) ||
                  (mode === "replace" &&
                    (!canRelease || !noteId || reason.trim().length < 20))
                }
                onClick={preview}
                className="rounded border px-3 py-2"
              >
                Preview supplier credit evidence
              </button>
              {proposal && (
                <div className="space-y-2 rounded border border-amber-300 p-3">
                  <p>
                    {proposal.mode !== "match"
                      ? `Document gross ₹${proposal.snapshot.total} — unmatched until separately reviewed`
                      : `Match ₹${proposal.snapshot.amount} · Document still unmatched ₹${proposal.snapshot.noteUnmatchedAfter}`}
                  </p>
                  <p>{proposal.warning}</p>
                  {proposal.mode === "replace" && (
                    <p>
                      Supersedes {proposal.snapshot.supersedesNoteId} · Reason:{" "}
                      {proposal.snapshot.replacementReason}. Original version is
                      retained and becomes unavailable for matching.
                    </p>
                  )}
                  {proposal.snapshot.allocations?.map((a) => (
                    <p key={sourceKey(a)} className="break-all">
                      {sourceLabel(a)} {sourceId(a)} · proposed match ₹{a.amount}
                    </p>
                  ))}
                  <label className="block">
                    <input
                      type="checkbox"
                      checked={!!checks.first}
                      onChange={(e) =>
                        setChecks((x) => ({ ...x, first: e.target.checked }))
                      }
                    />
                    {mode !== "match"
                      ? "I reviewed the actual supplier credit note, invoice reference and GST components."
                      : "I reviewed these exact posted return or dues-reduction credits and match amounts."}
                  </label>
                  <label className="block">
                    <input
                      type="checkbox"
                      checked={!!checks.second}
                      onChange={(e) =>
                        setChecks((x) => ({ ...x, second: e.target.checked }))
                      }
                    />
                    No new supplier credit, input GST, stock movement, payment
                    or refund is authorised.
                  </label>
                  {proposal.mode === "replace" && (
                    <label className="block">
                      <input
                        type="checkbox"
                        checked={!!checks.third}
                        onChange={(e) =>
                          setChecks((x) => ({ ...x, third: e.target.checked }))
                        }
                      />
                      I reviewed the old and replacement documents; no active
                      matches remain and the old version will be superseded.
                    </label>
                  )}
                  <button
                    type="button"
                    disabled={
                      busy ||
                      !checks.first ||
                      !checks.second ||
                      (proposal.mode === "replace" && !checks.third)
                    }
                    className="rounded bg-primary-700 px-3 py-2 text-white disabled:opacity-40"
                    onClick={save}
                  >
                    Retain supplier credit{" "}
                    {proposal.mode === "replace"
                      ? "replacement"
                      : proposal.mode === "note"
                        ? "document"
                        : "match"}
                  </button>
                </div>
              )}
            </fieldset>
          ) : (
            <p>
              Retaining or matching evidence requires accounting posting
              permission.
            </p>
          )}
        </div>
      )}
    </section>
  );
}
